import {
  ArrowLeftIcon,
  BookmarkIcon,
  BookOpenIcon,
  ChartBarIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  HeartIcon,
  PrinterIcon,
  ShareIcon,
  ShieldCheckIcon,
  TagIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import Loading from '../components/ui/Loading';
import apiService from '../services/api';
import { Protocol } from '../types';

const ProtocolDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [protocol, setProtocol] = useState<Protocol | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    const fetchProtocol = async () => {
      if (!id) {
        console.error('No protocol ID provided');
        return;
      }

      try {
        setIsLoading(true);
        console.log('Fetching protocol with ID:', id);
        // Use the ID directly (MongoDB ObjectIds are URL-safe)
        const protocolData = await apiService.getProtocol(id);
        console.log('Protocol data received:', protocolData);

        if (!protocolData) {
          throw new Error('Protocol data is null or undefined');
        }

        setProtocol(protocolData);

        // Set first section as active if sections exist
        if (protocolData.sections && Object.keys(protocolData.sections).length > 0) {
          const firstSection = Object.keys(protocolData.sections)[0];
          setActiveSection(firstSection);
        }

        // Increment view count
        try {
          await apiService.incrementViewCount(id);
        } catch (viewCountError) {
          // Don't fail the whole request if view count increment fails
          console.warn('Failed to increment view count:', viewCountError);
        }
      } catch (error: any) {
        console.error('Error fetching protocol:', error);
        console.error('Error details:', {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
        // Don't navigate away immediately - show error state
        setProtocol(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProtocol();
  }, [id]);

  // Scroll spy effect to update active section
  useEffect(() => {
    const handleScroll = () => {
      if (!protocol) return;

      const sections = Object.keys(protocol.sections);
      const scrollPosition = window.scrollY + 100; // Offset for better UX

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = document.getElementById(`section-${section}`);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [protocol]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'medical':
        return <HeartIcon className='h-6 w-6 text-red-500' />;
      case 'surgical':
        return <ShieldCheckIcon className='h-6 w-6 text-blue-500' />;
      case 'cardiac':
        return <HeartIcon className='h-6 w-6 text-red-600' />;
      case 'emergency':
        return <ExclamationTriangleIcon className='h-6 w-6 text-yellow-500' />;
      case 'pediatric':
        return <UserGroupIcon className='h-6 w-6 text-green-500' />;
      case 'geriatric':
        return <ChartBarIcon className='h-6 w-6 text-purple-500' />;
      default:
        return <BookOpenIcon className='h-6 w-6 text-gray-500' />;
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      medical: 'Médico',
      surgical: 'Quirúrgico',
      cardiac: 'Cardíaco',
      emergency: 'Emergencia',
      pediatric: 'Pediatría',
      geriatric: 'Geriatría',
    };
    return labels[category] || category;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      active: { label: 'Activo', className: 'badge-success' },
      draft: { label: 'Borrador', className: 'badge-warning' },
      archived: { label: 'Archivado', className: 'badge-secondary' },
      deprecated: { label: 'Deprecado', className: 'badge-danger' },
    };

    const config = statusConfig[status] || { label: status, className: 'badge-secondary' };
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  const getSectionIcon = (sectionType: string) => {
    switch (sectionType) {
      case 'description':
        return <DocumentTextIcon className='h-5 w-5' />;
      case 'clinical_manifestations':
        return <EyeIcon className='h-5 w-5' />;
      case 'diagnosis':
        return <ShieldCheckIcon className='h-5 w-5' />;
      case 'treatment':
        return <HeartIcon className='h-5 w-5' />;
      case 'medications':
        return <BookOpenIcon className='h-5 w-5' />;
      case 'procedures':
        return <ChartBarIcon className='h-5 w-5' />;
      case 'complications':
        return <ExclamationTriangleIcon className='h-5 w-5' />;
      case 'follow_up':
        return <ClockIcon className='h-5 w-5' />;
      default:
        return <DocumentTextIcon className='h-5 w-5' />;
    }
  };

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    // TODO: Implement bookmark functionality
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: protocol?.title,
        text: protocol?.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // TODO: Show toast notification
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <Layout showSidebar={true}>
        <div className='flex justify-center py-12'>
          <Loading size='lg' text='Cargando protocolo...' />
        </div>
      </Layout>
    );
  }

  if (!protocol) {
    return (
      <Layout showSidebar={true}>
        <div className='text-center py-12'>
          <DocumentTextIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
          <h3 className='text-lg font-medium text-gray-900 mb-2'>Protocolo no encontrado</h3>
          <p className='text-gray-600 mb-4'>
            El protocolo que buscas no existe o ha sido eliminado.
          </p>
          <Button variant='primary' onClick={() => navigate('/protocols')}>
            Ver Protocolos
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showSidebar={true}>
      <div className='max-w-7xl mx-auto'>
        {/* Header */}
        <div className='mb-6'>
          <Button variant='ghost' onClick={() => navigate(-1)} className='mb-4'>
            <ArrowLeftIcon className='h-4 w-4 mr-2' />
            Volver
          </Button>

          <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-8'>
            <div className='flex items-start justify-between mb-6 pb-6 border-b border-gray-200'>
              <div className='flex items-center space-x-3'>
                {getCategoryIcon(protocol.category)}
                <div>
                  <span className='text-sm font-semibold text-gray-700 uppercase tracking-wide'>
                    {getCategoryLabel(protocol.category)}
                  </span>
                  {protocol.subcategory && (
                    <span className='text-sm text-gray-500 ml-2 font-normal'>
                      • {protocol.subcategory}
                    </span>
                  )}
                </div>
                {getStatusBadge(protocol.status)}
              </div>

              <div className='flex items-center space-x-2'>
                <Button
                  variant='ghost'
                  onClick={handleBookmark}
                  className={isBookmarked ? 'text-insurance-600' : ''}
                >
                  <BookmarkIcon className='h-4 w-4' />
                </Button>
                <Button variant='ghost' onClick={handleShare}>
                  <ShareIcon className='h-4 w-4' />
                </Button>
                <Button variant='ghost' onClick={handlePrint}>
                  <PrinterIcon className='h-4 w-4' />
                </Button>
              </div>
            </div>

            <h1 className='text-4xl font-bold text-gray-900 mb-5 tracking-tight leading-tight'>
              {protocol.title}
            </h1>

            {protocol.description && (
              <p className='text-lg text-gray-700 mb-6 leading-relaxed'>{protocol.description}</p>
            )}

            <div className='flex flex-wrap items-center gap-4 text-sm text-gray-500'>
              <div className='flex items-center'>
                <EyeIcon className='h-4 w-4 mr-1' />
                {protocol.viewCount} vistas
              </div>
              <div className='flex items-center'>
                <ClockIcon className='h-4 w-4 mr-1' />
                Actualizado {new Date(protocol.updatedAt).toLocaleDateString('es-ES')}
              </div>
              <div className='flex items-center'>
                <UserIcon className='h-4 w-4 mr-1' />v{protocol.version}
              </div>
            </div>

            {protocol.tags && protocol.tags.length > 0 && (
              <div className='mt-4 flex flex-wrap gap-2'>
                {protocol.tags.map(tag => (
                  <span key={tag} className='badge badge-secondary text-xs'>
                    <TagIcon className='h-3 w-3 mr-1' />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div>
          <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-8'>
            <div className='space-y-16'>
              {protocol.sections ? (
                Object.entries(protocol.sections).map(([key, section], sectionIndex) => (
                  <div
                    key={key}
                    id={`section-${key}`}
                    className={`scroll-mt-6 ${
                      activeSection === key ? 'ring-2 ring-insurance-200 rounded-lg p-4 -m-4' : ''
                    }`}
                  >
                    {/* Section Header */}
                    <div className='mb-8 pb-4 border-b border-gray-200'>
                      <div className='flex items-center'>
                        {getSectionIcon(section.type)}
                        <h2 className='text-3xl font-bold text-gray-900 ml-3 tracking-tight'>
                          {section.title}
                        </h2>
                      </div>
                    </div>

                    {/* Section Content */}
                    <div className='prose prose-lg max-w-none'>
                      {section.content.map((paragraph, index) => (
                        <div key={index} className='mb-6'>
                          {paragraph.includes('\n') ? (
                            // Handle multi-line content with proper formatting
                            paragraph.split('\n').map((line, lineIndex) => {
                              const trimmedLine = line.trim();

                              // Handle subtitles (lines starting with ###)
                              if (trimmedLine.startsWith('###')) {
                                const subtitleText = trimmedLine.replace(/^###\s*/, '');
                                return (
                                  <h3
                                    key={lineIndex}
                                    className='text-xl font-bold text-gray-900 mt-8 mb-4 pt-4 border-t border-gray-100'
                                  >
                                    {subtitleText}
                                  </h3>
                                );
                              }

                              // Handle bullet points
                              if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('• ')) {
                                const bulletText = trimmedLine.replace(/^[-•]\s*/, '');
                                return (
                                  <div key={lineIndex} className='ml-6 mb-3 flex items-start'>
                                    <span className='text-insurance-600 mr-3 mt-1 text-lg'>•</span>
                                    <span className='text-gray-800 leading-relaxed text-base flex-1'>
                                      {bulletText}
                                    </span>
                                  </div>
                                );
                              }

                              // Handle numbered lists (1., 2., 3., etc.)
                              if (/^\d+\./.test(trimmedLine)) {
                                const match = trimmedLine.match(/^(\d+)\.\s*(.*)/);
                                if (match) {
                                  const number = match[1];
                                  const text = match[2];
                                  return (
                                    <div key={lineIndex} className='ml-6 mb-3 flex items-start'>
                                      <span className='text-insurance-600 mr-3 mt-0.5 font-semibold text-base min-w-[24px]'>
                                        {number}.
                                      </span>
                                      <span className='text-gray-800 leading-relaxed text-base flex-1'>
                                        {text}
                                      </span>
                                    </div>
                                  );
                                }
                              }

                              // Handle bold text (lines that might be headings)
                              if (trimmedLine && trimmedLine.length > 0) {
                                // Check if line looks like a heading (all caps or short line)
                                const isHeading =
                                  trimmedLine === trimmedLine.toUpperCase() ||
                                  (trimmedLine.length < 60 && !trimmedLine.includes('.'));

                                if (isHeading && lineIndex === 0) {
                                  return (
                                    <h4
                                      key={lineIndex}
                                      className='text-lg font-bold text-gray-900 mt-6 mb-3'
                                    >
                                      {trimmedLine}
                                    </h4>
                                  );
                                }

                                // Regular paragraph
                                return (
                                  <p
                                    key={lineIndex}
                                    className='mb-4 text-gray-800 leading-relaxed text-base'
                                  >
                                    {trimmedLine}
                                  </p>
                                );
                              }
                              return null;
                            })
                          ) : (
                            // Single line content
                            <p className='mb-4 text-gray-800 leading-relaxed text-base'>
                              {paragraph}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Divider between sections (except last) */}
                    {sectionIndex < Object.entries(protocol.sections).length - 1 && (
                      <div className='mt-12 pt-8 border-t border-gray-200' />
                    )}
                  </div>
                ))
              ) : (
                <div className='text-center py-12'>
                  <DocumentTextIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                  <h3 className='text-lg font-medium text-gray-900 mb-2'>
                    Contenido no disponible
                  </h3>
                  <p className='text-gray-600 mb-4'>
                    Este protocolo no tiene secciones detalladas disponibles en este momento.
                  </p>
                  <div className='bg-gray-50 rounded-lg p-4 text-left'>
                    <h4 className='font-medium text-gray-900 mb-2'>Información básica:</h4>
                    <p className='text-gray-700'>
                      {protocol.description || 'Sin descripción disponible'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Navigation between sections */}
          {protocol.sections && Object.keys(protocol.sections).length > 1 && (
            <div className='mt-6 flex justify-between'>
              <Button
                variant='secondary'
                disabled={activeSection === Object.keys(protocol.sections)[0]}
                onClick={() => {
                  const sections = Object.keys(protocol.sections);
                  const currentIndex = sections.indexOf(activeSection);
                  if (currentIndex > 0) {
                    setActiveSection(sections[currentIndex - 1]);
                  }
                }}
              >
                <ChevronRightIcon className='h-4 w-4 mr-2 rotate-180' />
                Anterior
              </Button>

              <Button
                variant='secondary'
                disabled={
                  activeSection ===
                  Object.keys(protocol.sections)[Object.keys(protocol.sections).length - 1]
                }
                onClick={() => {
                  const sections = Object.keys(protocol.sections);
                  const currentIndex = sections.indexOf(activeSection);
                  if (currentIndex < sections.length - 1) {
                    setActiveSection(sections[currentIndex + 1]);
                  }
                }}
              >
                Siguiente
                <ChevronRightIcon className='h-4 w-4 ml-2' />
              </Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ProtocolDetail;
